import React, { Fragment } from 'react';
import {
    InputAdornment,
    TextField,
    Checkbox,
    FormControlLabel,
    InputLabel as InputLabelMUI,
    Select,
    FormHelperText,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { rgba } from 'polished';
import classnames from 'classnames';
import { palette } from './colors';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import { observer } from 'mobx-react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import styled, { css } from 'styled-components';
import { FlexDiv } from './containers';
import moment from 'moment';
import { TextDefault } from './texts';

// making sure week on calendar starts on Monday
moment.updateLocale('en', {
    week: {
        dow: 1,
    },
});

const RadioTab = styled(FlexDiv)`
    transition: 0.3s;
    border-radius: 4px;
    cursor: pointer;
    padding: 16px;

    &:hover {
        background-color: white;
    }

    ${(props) =>
        props.addShadow &&
        css`
            box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
        `}
`;

const useStyles = makeStyles((theme) => ({
    inputLabel: {
        fontSize: '13px',
        fontWeight: 700,
        width: '100%',
        color: rgba(palette.graphite.heavyMetal, 0.6),
    },

    inputHelperText: {
        fontSize: '13px',
        width: '100%',
        color: rgba(palette.graphite.heavyMetal, 0.6),
    },

    multiselectError: {
        border: `1px solid ${palette.vibrant.red}`,
    },

    error: {
        color: palette.vibrant.red + ' !important',
    },

        hidden: {
            display: 'none',
        },

    inputStyles: {
        '& .MuiOutlinedInput-root': {
            color: palette.graphite.heavyMetal,
            fontSize: '14px',
            fontWeight: '400 !important',
            border: '1px solid #F7F7F7',
            '&.Mui-focused fieldset, &.Mui-error fieldset, &.MuiOutlinedInput-root fieldset':
                {
                    borderColor: 'transparent !important',
                },
            '&.Mui-focused': {
                border: `1px solid ${theme.palette.primary.main}`,
            },
            '&.Mui-error': {
                border: `1px solid ${palette.vibrant.red}`,
            },
            '&.Mui-disabled': {
                color: rgba(palette.graphite.heavyMetal, 0.6),
                backgroundColor: rgba(palette.graphite.heavyMetal, 0.02),
            },
        },
        '& .MuiFormLabel-shrink, .MuiFormLabel-root': {
            fontSize: '13px',
            color: rgba(palette.graphite.heavyMetal, 0.6),
            marginLeft: '2px',
            transform: 'none',
            fontWeight: 700,
            marginBottom: '4px',

            '&.Mui-error': {
                color: palette.vibrant.red,
            },
            '&.Mui-focused:not(.Mui-error)': {
                color: theme.palette.primary.main,
            },
        },
        '& .MuiInputBase-root': {
            height: '48px',
            background: 'transparent',
            minWidth: '240px',
            '& input': { paddingLeft: '12px' },
        },
        '& .MuiInputLabel-formControl': {
            position: 'static',
        },
        '& .MuiFormHelperText-root': {
            color: palette.vibrant.red,
            margin: '-6px 0 8px 2px',
            fontSize: '13px',
        },
    },
    heightSmall: {
        '& .MuiFormLabel-shrink': {
            marginTop: '2px',
        },
        '& .MuiInputBase-root': {
            height: '40px',
        },
    },
    heightLarge: {
        '& .MuiFormLabel-shrink': {
            marginTop: '10px',
        },
        '& .MuiInputBase-root': {
            height: '56px',
        },
    },
    widthSmall: {
        '& .MuiOutlinedInput-root': {
            maxWidth: '80px',
        },
        '& .MuiInputBase-root': {
            '& input': { padding: '0px !important' },
            minWidth: 'unset',
        },
    },
    inLine: {
        '& .MuiInputBase-root': {
            marginTop: '0px',
            marginBottom: '0px',
            minWidth: 'initial',
        },
    },
    calendarStyles: {
        '& .MuiSvgIcon-root': {
            fill: '#C4C4C4',
        },
    },
    withLeftIcon: {
        '& .MuiInputBase-root input': {
            paddingLeft: '0px',
        },
        '& .MuiOutlinedInput-adornedStart': {
            paddingLeft: '8px',
        },
        '& .MuiInputAdornment-positionStart': {
            marginRight: '2px',
        },
    },
    checkboxStyles: {
        '& .MuiFormControlLabel-label': {
            fontSize: '13px',
            fontFamily: 'Nunito Sans, sans-serif',
            color: palette.graphite.heavyMetal,
            fontWeight: 700,
            marginTop: '4px',
        },
        '& .MuiCheckbox-indeterminate': {
            color: theme.palette.primary.main,
        },
    },
    lighterLabel: {
        '& .MuiFormControlLabel-label': {
            color: rgba(palette.graphite.heavyMetal, 0.6),
        },
    },
    noYPadding: {
        marginBottom: '0px',

        '& .MuiCheckbox-root': {
            paddingTop: '0px',
            paddingBottom: '0px',
        },
    },
    airy: {
        '& .MuiOutlinedInput-root': {
            marginLeft: '16px',
        },
    },
    noMinWidth: {
        '& .MuiInputBase-root': {
            minWidth: 0,
        },
    },
    limitWidth: {
        '& .MuiOutlinedInput-root': {
            '&.MuiOutlinedInput-root.MuiOutlinedInput-root .MuiOutlinedInput-input':
                {
                    maxWidth: '192px',
                },
        },
    },
    selectWithIcons: {
        '& .MuiSelect-selectMenu': {
            whiteSpace: 'normal',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
        },
    },
    radiogroupStyles: {
        fontSize: '13px',
        fontFamily: 'Nunito Sans, sans-serif',
        color: palette.graphite.heavyMetal,
        fontWeight: 700,
    },
    textinputGlobalStylesOverwrite: {
        '& .MuiFormLabel-shrink, .MuiFormLabel-root': {
            color: palette.graphite.heavyMetal,
            marginBottom: '8px',
            fontFamily: 'unset',
            opacity: 0.6,

                '&.Mui-focused, &.Mui-error': {
                    opacity: 1,
                },
            },
            '& .MuiFormHelperText-root': {
                marginTop: '0px',
            },
        },
        selectGlobalStylesOverwrite: {
            '& .MuiInputBase-root > select': {
                border: 'none',
                background: 'transparent',
            },
        },

        multiselectOverwrite: {
            background: 'transparent',
        },

        darkerInputBorder: {
            '& .MuiOutlinedInput-root': {
                border: '1px solid #A5A5A5',
        },
    },
}));

export function InputLabel({ text, error }) {
    const classes = useStyles();
    return (
        <InputLabelMUI
            className={classnames(classes.inputLabel, {
                [classes.error]: !!error,
            })}
            error={!!error}
        >
            {text}
        </InputLabelMUI>
    );
}

export function InputHelperText({ text, error }) {
    const classes = useStyles();
    return (
        <FormHelperText
            className={classnames(classes.inputHelperText, {
                [classes.error]: !!error,
            })}
            error={!!error}
        >
            {text}
        </FormHelperText>
    );
}

export function TextInput({
    stretched,
    heightSmall,
    heightLarge,
    select,
    children,
    inline,
    airy,
    limitWidth,
    isMobile,
    overwriteGlobalStyles,
    widthSmall,
    leftIcon,
    hidden,
    darkerInputBorder,
    margin,
    id,
    ...props
}) {
    const classes = useStyles();
    let effectiveMargin = margin;
    if ( !effectiveMargin ) {
        effectiveMargin = !inline ? 'dense' : margin
    }
    return (
        <TextField
            className={classnames(classes.inputStyles, {
                [classes.hidden]: hidden,
                [classes.heightSmall]: heightSmall,
                [classes.heightLarge]: heightLarge,
                [classes.inLine]: inline,
                [classes.airy]: airy,
                [classes.widthSmall]: widthSmall,
                [classes.limitWidth]: limitWidth,
                [classes.noMinWidth]: isMobile,
                [classes.selectWithIcons]: select,
                [classes.withLeftIcon]: leftIcon,
                [classes.darkerInputBorder]: darkerInputBorder,
                [classes.textinputGlobalStylesOverwrite]: overwriteGlobalStyles,
                [classes.selectGlobalStylesOverwrite]:
                    overwriteGlobalStyles && isMobile,
            })}
            select={select}
            margin={effectiveMargin}
            variant="outlined"
            InputLabelProps={{
                shrink: true,

                // enable showing a tooltip in the label
                style: { pointerEvents: 'auto' },
            }}
            {...props}
            fullWidth={stretched}
            SelectProps={
                isMobile
                    ? { native: true }
                    : { MenuProps: { disableScrollLock: true } }
            }
            InputProps={
                leftIcon
                    ? {
                          startAdornment: (
                              <InputAdornment position="start">
                                  {leftIcon}
                              </InputAdornment>
                          ),
                      }
                    : undefined
            }
        >
            {children}
        </TextField>
    );
}

export function MultiSelectInput({
    stretched,
    heightSmall,
    heightLarge,
    children,
    inline,
    airy,
    limitWidth,
    isMobile,
    overwriteGlobalStyles,
    widthSmall,
    leftIcon,
    label,
    helperText,
    error,
    ...props
}) {
    const classes = useStyles();

    return (
        <>
            <TextDefault bold mb={4} className={classnames(classes.inputLabel, {
                [classes.error]: !!error})} color={palette.graphite.charcoal}>{label}</TextDefault>
            <Select
                className={classnames(classes.inputStyles, {
                    [classes.heightSmall]: heightSmall,
                    [classes.heightLarge]: heightLarge,
                    [classes.inLine]: inline,
                    [classes.airy]: airy,
                    [classes.widthSmall]: widthSmall,
                    [classes.limitWidth]: limitWidth,
                    [classes.noMinWidth]: isMobile,
                    [classes.withLeftIcon]: leftIcon,
                    [classes.textinputGlobalStylesOverwrite]:
                        overwriteGlobalStyles,
                    [classes.selectGlobalStylesOverwrite]:
                        overwriteGlobalStyles && isMobile,
                    [classes.multiselectOverwrite]: true,
                    [classes.multiselectError]: !!error,
                })}
                multiple
                variant="outlined"
                {...props}
                fullWidth={stretched}
            >
                {children}
            </Select>
            <InputHelperText error={error} text={helperText} />
        </>
    );
}

export const SearchInput = observer(
    ({ children, searchValue, onClear, ...props }) => {
        const classes = useStyles();

        return (
            <TextField
                className={classnames(classes.inputStyles, classes.inLine)}
                variant="outlined"
                style={{ cursor: 'pointer' }}
                value={searchValue}
                {...props}
                InputProps={{
                    endAdornment: (
                        <Fragment>
                            {searchValue.length > 0 && (
                                <InputAdornment
                                    position="start"
                                    onClick={onClear}
                                >
                                    <CancelIcon
                                        fontSize="small"
                                        style={{ cursor: 'pointer' }}
                                    />
                                </InputAdornment>
                            )}
                            <InputAdornment position="start">
                                <SearchIcon
                                    fontSize="small"
                                    style={{
                                        cursor: 'default',
                                    }}
                                />
                            </InputAdornment>
                        </Fragment>
                    ),
                }}
            >
                {children}
            </TextField>
        );
    }
);

export function CalendarInput({ format, datePickerOnly, isStatic, ...props }) {
    const classes = useStyles();

    const sharedCalendarProps = {
        className: classnames(classes.inputStyles, classes.calendarStyles),
        format: format || 'L',
        variant: isStatic ? 'static' : 'dialog',
        inputVariant: 'outlined',
        autoOk: true,
        disablePast: true,
        showTodayButton: true,
        cancelLabel: '',
        DialogProps: { disableScrollLock: true },
        InputProps: {
            endAdornment: (
                <InputAdornment position="start">
                    <CalendarTodayIcon />
                </InputAdornment>
            ),
        },
    };

    console.log('sharedCalendarProps', sharedCalendarProps);
    console.log('props', props);

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            {datePickerOnly ? (
                <DatePicker {...props} {...sharedCalendarProps}  value={moment(props.value)} />
            ) : (
                <DateTimePicker {...props} {...sharedCalendarProps} value={moment(props.value)} />
            )}
        </LocalizationProvider>
    );
}
export function CheckboxInput({ label, value, lighter, noYPadding, ...props }) {
    const classes = useStyles();

    return (
        <FormControlLabel
            className={classnames(classes.checkboxStyles, {
                [classes.lighterLabel]: lighter,
                [classes.noYPadding]: noYPadding,
            })}
            control={
                <Checkbox value={value || ''} color="primary" {...props} />
            }
            label={label}
        />
    );
}

export function RadioGroupInput({ label, value, children, ...props }) {
    const classes = useStyles();

    return (
        <FormControl component="fieldset">
            <FormLabel className={classnames(classes.radiogroupStyles, {})}>
                {label}
            </FormLabel>
            <RadioGroup value={value || ''} {...props}>
                {children}
            </RadioGroup>
        </FormControl>
    );
}

export function RadioTabInput({ buttonText1, buttonText2, boolean, onChange }) {
    return (
        <FlexDiv
            backgroundColor={palette.lightroom.heavyMetalLightHigh}
            borderRadius={8}
            padding={4}
            alignCenter
            gap={4}
            $noWrap
        >
            <RadioTab
                backgroundColor={
                    boolean ? palette.graphite.white : 'transparent'
                }
                addShadow={boolean}
                onClick={() => onChange(true)}
            >
                {buttonText1}
            </RadioTab>
            <RadioTab
                backgroundColor={
                    !boolean ? palette.graphite.white : 'transparent'
                }
                addShadow={!boolean}
                onClick={() => onChange(false)}
            >
                {buttonText2}
            </RadioTab>
        </FlexDiv>
    );
}
